import { numero, plural, rotulo } from '../../formato';

/**
 * O que as telas do CRM do Comercial dizem por extenso — onda 550 (Plano 2 ·
 * Comercial, Etapa 1, Tarefa 15). Os códigos do banco (0501) não aparecem na
 * tela (P-TXT-02): cada lista fechada tem a palavra dela aqui, num lugar só.
 */

/** A linha de serviço (`oportunidade.linha`). */
export const LINHA: { readonly [linha: string]: string } = {
  sondagem: 'Sondagem',
  laboratorio: 'Laboratório',
  controle_tecnologico: 'Controle tecnológico',
  investigacao_combinada: 'Investigação combinada',
  projetos_consultoria: 'Projetos e consultoria',
  outro: 'Outro',
};

/** Por onde a oportunidade chegou (`oportunidade.origem`). */
export const ORIGEM: { readonly [origem: string]: string } = {
  whatsapp: 'WhatsApp', email: 'E-mail', site: 'Formulário do site', telefone: 'Telefone',
  indicacao: 'Indicação', manual: 'Criada à mão',
};

/** Os estágios do quadro, na ordem do funil (§8.3); `descartada` fica fora do quadro. */
export const ESTAGIOS_DO_QUADRO = ['novo', 'triagem', 'em_proposta', 'enviada', 'negociacao', 'ganha', 'perdida'];

/** O motivo da perda (`oportunidade.motivo_perda`, RN-472 · RN-C03). */
export const MOTIVO_DA_PERDA: { readonly [motivo: string]: string } = {
  preco: 'Preço', prazo: 'Prazo', concorrente: 'Concorrente', desistiu: 'O cliente desistiu',
  sem_retorno: 'Sem retorno', escopo: 'Escopo', outro: 'Outro',
};

/**
 * O evento do funil que moveu o cartão (sistema e agente). A lista é a do
 * plano (Tarefa 17, `funil_evento`); a que a tela não conhece sai pelo rótulo.
 */
export const EVENTO_DO_FUNIL: { readonly [evento: string]: string } = {
  contato_novo: 'Contato novo',
  triagem_identificada: 'Linha e local identificados',
  rascunho_criado: 'Rascunho da proposta criado',
  proposta_enviada: 'Proposta enviada',
  pedido_de_ajuste: 'O cliente pediu ajuste',
  revisao_registrada: 'Revisão da proposta registrada',
  cliente_voltou: 'O cliente voltou a falar',
  aceite: 'Aceite da proposta',
  contrato_assinado: 'Contrato assinado',
  recusa_confirmada: 'Recusa confirmada',
  recusa_portal: 'Recusa no portal',
  sem_retorno: 'Sem retorno depois dos acompanhamentos',
  triagem_nao_comercial: 'Não é um pedido comercial',
};

/** Por onde a proposta foi ao cliente (`proposta_registrada.canal_envio`). */
export const CANAL_DE_ENVIO: { readonly [canal: string]: string } = {
  whatsapp: 'WhatsApp', email: 'E-mail', portal: 'Portal', em_maos: 'Em mãos', outro: 'Outro',
};

/** O tipo da tarefa (`tarefa.tipo`). */
export const TIPO_DE_TAREFA: { readonly [tipo: string]: string } = {
  acompanhamento: 'Acompanhamento', ligacao: 'Ligação', retorno: 'Retorno',
  confirmar_perda: 'Confirmar a perda', proposta_vencida: 'Proposta vencida', outro: 'Outro',
};

/** O papel do contato num cliente (`contato_cliente.papel`, §5.3 item 4). */
export const PAPEL: { readonly [papel: string]: string } = {
  contato: 'Contato do cliente', projetista: 'Projetista', consultor: 'Consultor',
  gerenciadora: 'Gerenciadora', fiscal: 'Fiscal da obra', compras: 'Compras',
};

/** O papel de cada chamada à IA (`agente_uso.papel`, §7.6): a aba Situação do agente e o painel. */
export const PAPEL_DA_IA: { readonly [papel: string]: string } = {
  resposta: 'Respostas', travas: 'Travas', classificacao: 'Classificação', resumo: 'Resumos',
  rascunho: 'Rascunhos', transcricao: 'Transcrição',
};

export const textoDe = (lista: { readonly [k: string]: string }, v: string | null | undefined) =>
  (v ? lista[v] ?? rotulo(v) : '');

/* O dinheiro mora no `formato.ts` (P-FMT-01); daqui só se reexporta, para as telas do Comercial não mudarem. */
export { reais, reaisRedondos } from '../../formato';

/** A proposta pelo nome que ela tem no papel: «P-26101639-R01». */
export const nomeDaProposta = (numeroDaProposta: string, revisao: number) =>
  `${numeroDaProposta}-R${String(revisao).padStart(2, '0')}`;

/** O local da obra numa linha: «Rua X · Blumenau/SC». */
export const localDaObra = (l: { endereco?: string | null; municipio?: string | null; uf?: string | null }) =>
  [l.endereco, [l.municipio, l.uf].filter(Boolean).join('/')].filter(Boolean).join(' · ');

export interface Pessoa { id: string; nome: string }

export interface ItemDaOportunidade {
  id: string;
  contato: { id: string; nome: string };
  cliente: { id: string; nome: string; estrategico: boolean } | null;
  linha: string | null;
  local: { endereco: string | null; municipio: string | null; uf: string | null };
  estagio: string;
  estagioDesde: string;
  origem: string;
  responsavel: Pessoa | null;
  indicadaPor: Pessoa | null;
  proximaAcao: string | null;
  proximaAcaoEm: string | null;
  criadaEm: string;
  /** Só para a diretoria e o responsável (M11). */
  valorEstimado?: number | null;
  valorPelaRegra?: boolean;
}

/** O cartão do quadro do funil (`GET /comercial/funil`, Tarefa 18). */
export interface CartaoDoFunil {
  id: string;
  contato: { id: string; nome: string };
  cliente: { id: string; nome: string; estrategico: boolean } | null;
  linha: string | null;
  municipio: string | null;
  /** Só para quem tem o Comercial (`crm.ver`, P2-22). */
  valorEstimado?: number | null;
  valorPelaRegra?: boolean;
  responsavel: Pessoa | null;
  diasNoEstagio: number;
  proximaAcao: { texto: string; em: string | null } | null;
  motivoPerda: string | null;
  /** Do Radar (Tarefa 19): o item mais grave, em risco ou crítico, da proposta ou de uma tarefa vencida. */
  destaque: { nivel: string; motivo: string } | null;
  podeMover: boolean;
}

export interface ColunaDoFunil {
  estagio: string;
  /** Quantas há de verdade; a coluna traz até 200 cartões. */
  total: number;
  soma?: number;
  cartoes: CartaoDoFunil[];
}

/* O item do Radar e a tradução dele em cartão da pilha moram no adaptador do Radar (`painel/doRadar.ts`,
   R-M2 da re-revisão: o Início do Hub não importa o Comercial); daqui só se reexporta. */
export {
  FALTA_NO_RADAR, GRAVIDADE_DO_NIVEL, outrasPendencias, pendenciaDoRadar, quemAgeNoRadar, tempoNoRadar, type ItemDoRadar,
  type LinhaDoRadar,
} from '../../painel/doRadar';

export interface LimitesDoRadar {
  respostaHorasUteis: number; propostaDias: number; emRiscoFracao: number; tarefaHorasUteis: number;
}

/** A regra do Radar como está nos ajustes: «Crítico: … Em risco a partir da metade…» (a tela e o «i» do painel). */
export const regraDoRadar = (l: LimitesDoRadar) => {
  const fracao = l.emRiscoFracao === 0.5 ? 'da metade' : `de ${numero(l.emRiscoFracao * 100, 0)} %`;
  return `Crítico: ${plural(l.respostaHorasUteis, 'hora útil', 'horas úteis')} sem a nossa resposta, `
    + `${plural(l.propostaDias, 'dia', 'dias')} de proposta parada ou `
    + `${plural(l.tarefaHorasUteis * 2, 'hora útil', 'horas úteis')} de tarefa vencida. `
    + `Em risco a partir ${fracao} (a tarefa, com ${plural(l.tarefaHorasUteis, 'hora útil', 'horas úteis')}).`;
};

export interface Movimento {
  id: string; de: string | null; para: string; origem: 'sistema' | 'agente' | 'pessoa';
  evento: string | null; motivo: string | null; em: string; quem: Pessoa | null;
}

export interface PropostaRegistrada {
  id: string; numero: string; revisao: number; validade: string; enviadaEm: string; canalEnvio: string;
  arquivo: { id: string; nome: string; tamanho: number };
  valor?: number;
}

export interface Tarefa {
  id: string; tipo: string; titulo: string; venceEm: string; situacao: string; vencida: boolean;
  feitaEm: string | null; motivoCancelamento: string | null;
  responsavel: { tipo: 'pessoa' | 'agente'; id: string | null; nome: string | null };
  oportunidade?: { id: string; contato: string; estagio: string } | null;
  conversa?: { id: string; contato: string; canal: string } | null;
}

export interface DetalheDaOportunidade {
  oportunidade: ItemDaOportunidade & {
    escopo: string | null; furos: number | null; areaM2: number | null; acompanhamentoPessoal: boolean;
    conversaOrigemId: string | null; motivoPerda: string | null; motivoPerdaTexto: string | null;
    podeEditar: boolean; veValor: boolean;
  };
  movimentos: Movimento[];
  propostas: PropostaRegistrada[];
  tarefas: Tarefa[];
  conversas: { id: string; situacao: string; abertaEm: string; ultimaAtividade: string;
    canal: { codigo: string; nome: string } }[];
}
